import { useMemo } from 'react';

import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';

import { PanelGrid } from 'components/grid';
import { Block } from 'components/page';
import { TituloListBoletoBtn, TituloListStatusColumn } from 'pages/financeiro/titulo/components/';
import { TituloListFppBtn } from 'pages/financeiro/titulo/components/atoms/TituloListActionPrint';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useQueryTitulos } from 'pages/financeiro/titulo/hooks';
import { TituloModelProps } from 'pages/financeiro/titulo/types';
import { brlDate, brlPrice } from 'util/format';

const getRows = (list: TituloModelProps[] | undefined): GridRowsProp => {
    if (Array.isArray(list) && list.length) {
        return list.map((item, index) => ({
            id: index + 1,
            nrFpp: item.nrFpp,
            nrParcela: item.nrParcela,
            nrTitulo: item.nrTitulo,
            nrNotaFiscal: item.nrNotaFiscal,
            dtEmissao: item.dtEmissao && `${item.dtEmissao}T00:00:00`,
            dtPagamento: item.dtPagamento && brlDate(`${item.dtPagamento}T00:00:00`),
            dtVencimento: item.dtVencimento && brlDate(`${item.dtVencimento}T00:00:00`),
            dtCompensacao: item.dtCompensacao && brlDate(`${item.dtCompensacao}T00:00:00`),
            dtVencimentoFormatada: item.dtVencimentoFormatada,
            tpStatus: item.tpStatus,
            tpNota: item.tituloTipoNota,
            tpTitulo: item.origemTitulo.dsOrigemTitulo,
            fgImpresso: item.fgImpresso,
            vlTitulo: item.vlTitulo,
            boleto: item.boleto,
        }));
    }

    return [];
};

export function TituloGrid() {
    const { hasPermission } = usePermissionsContext();
    const { isFetching, data, refetch } = useQueryTitulos();

    const columns: GridColDef[] = useMemo(() => {
        const objColumns: GridColDef[] = [
            { field: 'nrNotaFiscal', headerName: 'Nota fiscal', minWidth: 160 },
            {
                field: 'nrTitulo',
                headerName: 'N° do título/parcela',
                minWidth: 180,
                renderCell: ({ row }) => `${row.nrTitulo}/${row.nrParcela}`,
            },
            { field: 'dtEmissao', headerName: 'Emissão', width: 100, minWidth: 90, valueFormatter: (value) => value && brlDate(value) },
            { field: 'dtVencimento', headerName: 'Vencimento', minWidth: 90 },
            // { field: 'dtPagamento', headerName: 'Data Pagamento', minWidth: 90 }, // api está com erro
            { field: 'dtCompensacao', headerName: 'Data de compensação', minWidth: 90 },
            { field: 'tpTitulo', headerName: 'Tipo de título', minWidth: 140 },
            { field: 'tpNota', headerName: 'Tipo de nota', minWidth: 80 },
            { field: 'vlTitulo', headerName: 'Valor', type: 'number', flex: 1, minWidth: 90, valueFormatter: (value) => brlPrice(value) },
            {
                field: 'tpStatus',
                headerName: 'Status',
                headerAlign: 'center',
                minWidth: 140,
                renderCell: ({ row }) => <TituloListStatusColumn status={row.tpStatus} />,
            },
        ];

        if (hasPermission(['FINANCEIRO_TITULO_BOLETO_GERAR'])) {
            objColumns.push(
                {
                    field: 'dsBoleto',
                    align: 'center',
                    headerAlign: 'center',
                    headerName: 'Boleto',
                    filterable: false,
                    sortable: false,
                    hideable: false,
                    disableColumnMenu: true,
                    minWidth: 110,
                    renderCell: (params) => <TituloListBoletoBtn params={params} />,
                },
                {
                    field: 'dsImrpimir',
                    align: 'center',
                    headerAlign: 'center',
                    headerName: 'Nota de débito',
                    filterable: false,
                    sortable: false,
                    hideable: false,
                    disableColumnMenu: true,
                    minWidth: 110,
                    renderCell: (params) => <TituloListFppBtn params={params} />,
                },
            );
        }

        return objColumns;
    }, []);

    return (
        <Block>
            <PanelGrid rows={getRows(data?.data)} reload={refetch} loading={isFetching} columns={columns} remote={false} summary={['vlTitulo']} />
        </Block>
    );
}
